// V2 Activity timeline. Renders the policy-change activities as a compact
// timeline list (time on the left, title + "actor · source" subtitle), inside
// the V2 SectionPanel — matching the mockup. Uses the same activity data
// source as PolicyChangeActivities, with a "Create" button in the header to
// add a new activity (opens the shared ManageBusinessTask dialog).

import React from "react";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../../../../hooks";
import {
  clearActivityState,
  fetchBusinessTasksAsync,
} from "../../../../../../../../redux/activity/activityActions";
import {
  IBusinessTask,
  INITIAL_BUSINESS_TASK,
} from "../../../../../../../../redux/activity/activity.types";
import { LoadState, MODULE_NAMES } from "../../../../../../../../constants/enums";
import { formatTime } from "../../../../../../../../helpers";
import { ManageBusinessTask } from "../../../../../../../../components/Activity/BusinessTasks/ManageBusinessTask";
import { IActivityModuleInformation } from "../../../../../../../../components/Activity/Activity.types";
import { SectionPanel } from "./SectionPanel";

interface IActivityTimelineV2Props {
  moduleId: string;
  customerId?: number;
  customerPolicyId?: number;
}

// Builds the "actor · source" subtitle from whatever fields are present.
const buildSubtitle = (task: IBusinessTask): string => {
  const actor = task.created_by_name || task.assigned_to_name || "System";
  const source = task.branch_code
    ? `${task.branch_code} branch`
    : task.task_type || "";
  return [actor, source].filter(Boolean).join(" · ");
};

export const ActivityTimelineV2: React.FC<IActivityTimelineV2Props> = ({
  moduleId,
  customerId,
  customerPolicyId,
}) => {
  const dispatch = useDispatchWrapper();

  const { data, dataLoading } = useSelector(
    (storeState: IStoreState) => storeState.acitivty.tasks
  );

  const [openCreate, setOpenCreate] = React.useState(false);
  const [selectedTask, setSelectedTask] = React.useState<IBusinessTask | null>(
    null
  );

  const moduleInformation: IActivityModuleInformation = {
    moduleId,
    moduleName: MODULE_NAMES.POLICY_CHANGE,
    subModuleName: MODULE_NAMES.POLICY_CHANGE,
    refColumnName: "change_request_code",
  };

  const loadActivities = React.useCallback(() => {
    dispatch(fetchBusinessTasksAsync(moduleId));
  }, [dispatch, moduleId]);

  React.useEffect(() => {
    loadActivities();
    return () => {
      dispatch(clearActivityState());
    };

  }, [moduleId]);

  const isLoading = dataLoading === LoadState.InProgress;

  return (
    <>
      <SectionPanel
        heading="Activity"
        sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}
        bodySx={{ p: 0, flexGrow: 1, display: "flex", flexDirection: "column" }}
        // Create lives in the blue header (right side), keeping the body a
        // clean timeline list under the header — matching the mockup.
        headerAction={
          <Button
            variant="text"
            size="small"
            startIcon={<Add />}
            onClick={() => {
              setSelectedTask(null);
              setOpenCreate(true);
            }}
            sx={{
              textTransform: "none",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            Create
          </Button>
        }
      >
        {isLoading && (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 120,
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">Loading activity…</Typography>
          </Box>
        )}

        {!isLoading && data.length === 0 && (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 120,
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">No activity yet.</Typography>
          </Box>
        )}

        {!isLoading &&
          data.map((task, index) => {
            const time = formatTime(task.create_ts || task.insert_ts || null);
            return (
              <Box key={task.task_module_wise_code || index}>
                {index > 0 && <Divider />}
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                  alignItems="flex-start"
                  onClick={() => {
                    setSelectedTask(task);
                    setOpenCreate(true);
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 48, pt: "1px", whiteSpace: "nowrap" }}
                  >
                    {time || "--"}
                  </Typography>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {task.task_name || task.description || "Activity"}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {buildSubtitle(task)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          })}
      </SectionPanel>

      {openCreate && (
        <ManageBusinessTask
          open={openCreate}
          task={
            selectedTask ?? {
              ...INITIAL_BUSINESS_TASK,
              module_reference_code_or_id: moduleId,
            }
          }
          moduleInformation={moduleInformation}
          customer_id={customerId}
          customer_policy_id={customerPolicyId}
          onClose={() => {
            setOpenCreate(false);
            setSelectedTask(null);
          }}
          onComplete={() => {
            setOpenCreate(false);
            setSelectedTask(null);
            loadActivities();
          }}
        />
      )}
    </>
  );
};
